import React, { useEffect, useCallback, useMemo } from 'react';
import { Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import { Colors, Text, Checkbox } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import Dropdown from 'components/elements/Dropdown';
import DropdownItem from 'components/elements/Dropdown/DropdownItem';
import InfoMarker from 'components/elements/InfoMarker';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useProjectSize } from 'state/dashboard';
import {
  textInputToFormattedIntegerMiddleware,
  removeCommas,
  formatNumberWithCommas,
} from 'utils/formMiddleware';

const menuContainerStyle = {
  rowGap: 'padding_12',
};

const inputStyle = {
  border: '2px solid #EAECEA',
  borderRadius: '4px',
};

const includeUnknownValueStyle = {
  columnGap: 'padding_4',
};

const ProjectSizeFilter = () => {
  const {
    projectSizeMin,
    setProjectSizeMin,
    projectSizeMax,
    setProjectSizeMax,
    projectSizeIncludeUnknown,
    setProjectSizeIncludeUnknown,
  } = useProjectSize();

  const { handleSubmit, register, reset } = useForm({
    mode: 'onChange',
    defaultValues: {
      minSize:
        projectSizeMin && Number(projectSizeMin) > 0
          ? formatNumberWithCommas(Number(projectSizeMin))
          : '',
      maxSize:
        projectSizeMax && Number(projectSizeMax) > 0
          ? formatNumberWithCommas(Number(projectSizeMax))
          : '',
    },
  });

  useEffect(() => {
    // if saved cost changes reset form
    reset({
      minSize:
        projectSizeMin && Number(projectSizeMin) > 0
          ? formatNumberWithCommas(Number(projectSizeMin))
          : '',
      maxSize:
        projectSizeMax && Number(projectSizeMax) > 0
          ? formatNumberWithCommas(Number(projectSizeMax))
          : '',
    });
  }, [projectSizeMin, projectSizeMax, reset]);

  const filterCount = useMemo(() => {
    let count = 0;

    if (Number(projectSizeMin) > 0) {
      count += 1;
    }
    if (Number(projectSizeMax) > 0) {
      count += 1;
    }

    return count;
  }, [projectSizeMin, projectSizeMax, projectSizeIncludeUnknown]);

  const onSubmit = useCallback(
    (data: { minSize?: string; maxSize?: string }) => {
      const minValue = data.minSize ? Number(removeCommas(String(data.minSize))) : 0;
      const maxValue = data.maxSize ? Number(removeCommas(String(data.maxSize))) : 0;
      setProjectSizeMin(Math.max(minValue, 0));
      setProjectSizeMax(Math.max(maxValue, 0));
    },
    [setProjectSizeMin, setProjectSizeMax],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit(onSubmit)();
    }
  };

  const handleOnBlur = () => {
    handleSubmit(onSubmit)();
  };

  return (
    <Dropdown title="Project Sqft" filterCount={filterCount}>
      <DropdownItem>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexColumn sx={menuContainerStyle}>
            <FlexColumn>
              <Text.SmallLabel color={Colors.Black800}>Min</Text.SmallLabel>
              <InputGroup size="md">
                <InputRightElement pointerEvents="none">sqft</InputRightElement>
                <Input
                  size="md"
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('minSize', {
                    onChange: textInputToFormattedIntegerMiddleware,
                  } as any)}
                  onKeyPress={handleKeyPress}
                  onBlur={handleOnBlur}
                  type="text"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                />
              </InputGroup>
            </FlexColumn>
            <FlexColumn>
              <Text.SmallLabel color={Colors.Black800}>Max</Text.SmallLabel>
              <InputGroup size="md">
                <InputRightElement pointerEvents="none">sqft</InputRightElement>
                <Input
                  sx={inputStyle}
                  border="1px solid"
                  focusBorderColor="brand.green"
                  errorBorderColor="brand.red"
                  {...register('maxSize', {
                    onChange: textInputToFormattedIntegerMiddleware,
                  } as any)}
                  onKeyPress={handleKeyPress}
                  onBlur={handleOnBlur}
                  type="text"
                  onWheel={(e) => (e.target as HTMLElement).blur()}
                />
              </InputGroup>
            </FlexColumn>
            <Checkbox
              isChecked={projectSizeIncludeUnknown}
              onToggle={() => {
                setProjectSizeIncludeUnknown(!projectSizeIncludeUnknown);
              }}
            >
              <FlexRow sx={includeUnknownValueStyle}>
                <Text>Include unknown values</Text>
                <InfoMarker label="Data availability varies by city. We recommend including unknown values to broaden search results when using multiple filters." />
              </FlexRow>
            </Checkbox>
          </FlexColumn>
        </form>
      </DropdownItem>
    </Dropdown>
  );
};

export default ProjectSizeFilter;
